import React, { useState, useRef, useEffect } from 'react';
import { MessageSquareIcon, MoreVerticalIcon, Edit2Icon, XIcon } from './Icons';

interface Conversation {
  id: string;
  name: string;
  lastMessage?: string;
  updatedAt: string | Date;
}

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
  onRename?: (id: string, newName: string) => void;
  onDelete?: (id: string) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  onClick,
  onRename,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(conversation.name);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleRenameSubmit = () => {
    if (renameValue.trim() && renameValue !== conversation.name) {
      onRename?.(conversation.id, renameValue.trim());
    }
    setIsRenaming(false);
    setShowMenu(false);
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: isActive ? 'rgba(51,170,64,0.15)' : isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
    borderLeft: isActive ? '3px solid #33AA40' : '3px solid transparent',
    position: 'relative',
    transition: 'background-color 0.15s ease',
  };

  const iconContainerStyle: React.CSSProperties = {
    flexShrink: 0,
    color: isActive ? '#33AA40' : '#8899A6',
  };

  const textContainerStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  };

  const nameStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#ffffff' : '#C8D6DF',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const moreButtonStyle: React.CSSProperties = {
    display: isHovered || showMenu ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: 'none',
    background: 'rgba(255,255,255,0.1)',
    cursor: 'pointer',
    padding: 0,
    color: '#C8D6DF',
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: '8px',
    backgroundColor: '#2B3B44',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 100,
    overflow: 'hidden',
    minWidth: '120px',
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    fontSize: '12px',
    color: '#C8D6DF',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '4px 8px',
    fontSize: '13px',
    backgroundColor: '#30424D',
    border: '1px solid #33AA40',
    borderRadius: '4px',
    color: '#ffffff',
    outline: 'none',
  };

  if (isRenaming) {
    return (
      <div style={containerStyle}>
        <input
          ref={inputRef}
          style={inputStyle}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleRenameSubmit();
            if (e.key === 'Escape') {
              setRenameValue(conversation.name);
              setIsRenaming(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setShowMenu(false); }}
    >
      <div style={iconContainerStyle}>
        <MessageSquareIcon size={16} color={isActive ? '#33AA40' : '#8899A6'} />
      </div>
      <div style={textContainerStyle}>
        <div style={nameStyle}>{conversation.name}</div>
      </div>
      <button
        style={moreButtonStyle}
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
      >
        <MoreVerticalIcon size={14} color="#C8D6DF" />
      </button>
      {showMenu && (
        <div ref={menuRef} style={menuStyle}>
          <button
            style={menuItemStyle}
            onClick={(e) => {
              e.stopPropagation();
              setIsRenaming(true);
              setShowMenu(false);
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.05)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <Edit2Icon size={12} color="#C8D6DF" />
            Rename
          </button>
          <button
            style={{ ...menuItemStyle, color: '#FF5656' }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(conversation.id);
              setShowMenu(false);
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,86,86,0.1)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <XIcon size={12} color="#FF5656" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationItem;
